// app/components/Footer.tsx
"use client";
import { useTranslations, useLocale } from "next-intl";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import { Typography } from "@mui/material";
import Link from "@mui/material/Link";
import Image from "next/image";

// ⬇️ ดึง version จาก package.json
import packageJson from "../../package.json";

function Footer() {
  const t = useTranslations("Menu");
  const year = new Date().getFullYear();
  const locale = useLocale(); // "en" | "zh"
  const appVersion = packageJson.version; 

  const base = (path: string) => {
    const seg = path === "/" ? "" : path.startsWith("/") ? path : `/${path}`;
    if (locale === "en") return seg || "/"; 
    return `/${locale}${seg}`; 
  };

  return (
    <Box className="footer-wrap">
      <Box sx={{ background: "#242424", py: 5 }}>
        <Container maxWidth="lg">
          <Grid
            container
            sx={{
              display: { lg: "flex" },
              alignItems: "center",
              alignContent: "center"
            }}>
            <Grid size={{ xs: 12, sm: 6, lg: "grow" }} sx={{
              display: { lg: "flex" }
            }}>
              <Link
                href="https://www.poonphol.com/en/home"
                aria-label="Tapioca"
                sx={{ display: { lg: "flex" } }}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Image
                  src={"/assets/logo-white.svg"}
                  title="Poonphol Tapioca Logo"
                  alt="Poonphol Tapioca Logo"
                  width={173}
                  height={66}
                  priority
                />
              </Link>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, lg: "auto" }}>
              {/* ===== Desktop links ===== */}
              <Box sx={{
                display: { xs: "none", lg: "block" }
              }}>
                <Link
                  href={base("/thailand-premium-tapioca-starch-products")}
                  sx={{
                    ml: { lg: 4 },
                    mb: { xs: 2, lg: 0 },
                    display: { xs: "flex", lg: "inline-block" }
                  }}>
                  {t("Products")}
                </Link>
                <Link
                  href={base("/thailand-tapioca-starch-manufacturing-export-services-fcl-lcl")}
                  sx={{
                    ml: { lg: 4 },
                    mb: { xs: 2, lg: 0 },
                    display: { xs: "flex", lg: "inline-block" }
                  }}>
                  {t("Services")}
                </Link>
                <Link
                  href={base("/native-tapioca-starch-manufacturing-process")}
                  sx={{
                    ml: { lg: 4 },
                    mb: { xs: 2, lg: 0 },
                    display: { xs: "flex", lg: "inline-block" }
                  }}>
                  {t("ManufacturingProcess")}
                </Link>
                <Link
                  href={base("/news")}
                  sx={{
                    ml: { lg: 4 },
                    mb: { xs: 2, lg: 0 },
                    display: { xs: "flex", lg: "inline-block" }
                  }}>
                  {t("News")}
                </Link>
                <Link
                  href={base("/faq")}
                  sx={{
                    ml: { lg: 4 },
                    mb: { xs: 2, lg: 0 },
                    display: { xs: "flex", lg: "inline-block" }
                  }}>
                  {t("FAQ")}
                </Link>
                <Link
                  href={base("/contact-us")}
                  sx={{
                    ml: { lg: 4 },
                    mb: { xs: 2, lg: 0 },
                    display: { xs: "flex", lg: "inline-block" }
                  }}>
                  {t("Contact")}
                </Link>
              </Box>

              {/* ===== Mobile links ===== */}
              <Grid
                container
                sx={{
                  display: { xs: "flex", lg: "none" },
                  pt: 5
                }}>
                <Grid size={6}>
                  <Link
                    href={base("/thailand-premium-tapioca-starch-products")}
                    sx={{
                      ml: { lg: 4 },
                      mb: { xs: 2, lg: 0 },
                      display: { xs: "flex", lg: "inline-block" }
                    }}>
                    {t("Products")}
                  </Link>
                  <Link
                    href={base("/thailand-tapioca-starch-manufacturing-export-services-fcl-lcl")}
                    sx={{
                      ml: { lg: 4 },
                      mb: { xs: 2, lg: 0 },
                      display: { xs: "flex", lg: "inline-block" }
                    }}>
                    {t("Services")}
                  </Link>
                  <Link
                    href={base("/native-tapioca-starch-manufacturing-process")}
                    sx={{
                      ml: { lg: 4 },
                      mb: { xs: 0, lg: 0 },
                      display: { xs: "flex", lg: "inline-block" }
                    }}>
                    {t("ManufacturingProcess")}
                  </Link>
                </Grid>
                <Grid size={6}>
                  <Link
                    href={base("/news")}
                    sx={{
                      ml: { lg: 4 },
                      mb: { xs: 2, lg: 0 },
                      display: { xs: "flex", lg: "inline-block" }
                    }}>
                    {t("News")}
                  </Link>
                  <Link
                    href={base("/faq")}
                    sx={{
                      ml: { lg: 4 },
                      mb: { xs: 2, lg: 0 },
                      display: { xs: "flex", lg: "inline-block" }
                    }}>
                    {t("FAQ")}
                  </Link>
                  <Link
                    href={base("/contact-us")}
                    sx={{
                      ml: { lg: 4 },
                      mb: { xs: 2, lg: 0 },
                      display: { xs: "flex", lg: "inline-block" }
                    }}>
                    {t("Contact")}
                  </Link>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </Box>
      <Box sx={{ background: "#151414", textAlign: { xs: "center", lg: "left" } }}>
        <Container className="footer">
          <Grid
            container
            sx={{
              display: { xs: "block", md: "flex" },
              alignItems: "center",
              py: 3
            }}>
            <Grid size={{ xs: 12, lg: "grow" }}>
              <Typography
                variant="body1"
                sx={{ color: "rgba(255,255,255,0.37) !important" }}
              >
                &copy;{year} Poon Phol Company Limited. All Rights Reserved.{" "}
                v{appVersion}
              </Typography>
            </Grid>

            <Grid
              size={{ xs: 12, lg: "auto" }}
              className="footer-social"
              sx={{
                display: { xs: "block", sm: "flex" },
                justifyContent: { xs: "center", lg: "flex-end" },
                alignItems: "center",
                pt: { xs: 0, sm: 2, lg: 0 }
              }}>
              <Box
                sx={{
                  display: "block",
                  py: { xs: 3, sm: 0 }
                }}>
                <Link
                  href={base("/cookies-policy")}
                  sx={{
                    mx: 2,
                    display: { xs: "block", sm: "inline-block" }
                  }}>
                  {t("CookiesPolicy")}
                </Link>
                <Typography
                  component="span"
                  sx={{
                    display: { xs: "none", lg: "inline-block" }
                  }}
                >
                  |
                </Typography>
                <Link
                  href={base("/terms-and-conditions")}
                  sx={{
                    mx: 2,
                    display: { xs: "block", sm: "inline-block" }
                  }}>
                  {t("TermsAndConditions")}
                </Link>
                <Typography
                  component="span"
                  sx={{
                    display: { xs: "none", lg: "inline-block" }
                  }}
                >
                  |
                </Typography>
                <Link
                  href={base("/privacy-policy")}
                  sx={{
                    mx: 2,
                    display: { xs: "block", sm: "inline-block" }
                  }}>
                  {t("PrivacyPolicy")}
                </Link>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
}
export default Footer;
